import React from 'react';
import { FormProvider } from 'react-hook-form';
import { GovMessage } from '@gov-design-system-ce/react';
import { useTranslationWithNamespace } from '@gov-nx/core/service';
import type { PageCode } from '@gov-nx/module/page';
import { useNastaveniUpozorneniContextInstance } from '@gov-nx/module/page';
import {
	ContentLayout,
	DataTable,
	FormSwitch,
	Icon,
	LayoutGap,
} from '@gov-nx/ui/web';
import { SettingsNotificationFormSkeleton } from './SettingsNotificationFormSkeleton';

export interface SettingsNotificationFormProps {
	code: PageCode;
}

export const SettingsNotificationForm = ({
	code,
}: SettingsNotificationFormProps) => {
	const { getLocalizeCurried } = useTranslationWithNamespace();
	const tsn = getLocalizeCurried(code);
	const {
		allKeys,
		allTypes,
		formMethods,
		handleChange,
		controls,
		noContactDetails,
	} = useNastaveniUpozorneniContextInstance();

	if (controls.initialLoading) {
		return <SettingsNotificationFormSkeleton />;
	}

	return (
		<LayoutGap gap="m">
			<ContentLayout>
				<FormProvider {...formMethods}>
					<form autoComplete="off">
						<LayoutGap gap="xl">
							<LayoutGap gap="s">
								<h2 className="text-headline-m! mb-2">
									{tsn('table.header.titulek')}
								</h2>
								<p className="mt-0!">{tsn('table.header.subtitulek')}</p>
							</LayoutGap>
							{noContactDetails ? (
								<GovMessage
									color="warning"
									type="subtle"
									icon={
										<Icon
											type="components"
											name="info-circle-fill"
										/>
									}>
									{tsn('upozorneni.zprava.zadne-kontaktni-udaje')}
								</GovMessage>
							) : null}
							<div className="gov-table">
								<DataTable>
									<thead className="text-body-s [ md:text-body-m ]">
										<tr>
											<th className="w-auto">
												<span className="invisible">
													{tsn('table.header.druh')}
												</span>
											</th>
											<th className="font-normal w-[65px] py-2">
												{tsn('table.header.email')}
											</th>
											<th className="font-normal w-[65px] py-2">
												{tsn('table.header.sms')}
											</th>
										</tr>
									</thead>
									<tbody>
										{allKeys.map((name) => (
											<tr key={name}>
												<td className="text-body-s-bold">
													{tsn(`table.body.${name}`)}
												</td>
												{allTypes.map(({ type, isDisabled }) => (
													<td key={`${name}.${type}`}>
														<FormSwitch
															field={{
																onChange: () => handleChange(name, type),
																name: `${name}.${type}`,
																isDisabled:
																	isDisabled || controls.processLoading,
																hasNoLabel: true,
																'aria-label': `${type}`,
															}}
														/>
													</td>
												))}
											</tr>
										))}
									</tbody>
								</DataTable>
							</div>
						</LayoutGap>
					</form>
				</FormProvider>
			</ContentLayout>
		</LayoutGap>
	);
};
